import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircleIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { languageOptions } from '@constants/languages.constant';
import { providerSpecialtyOptions } from '@constants/providers.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import {
  updateProviderSchema,
  type UpdateProviderDto,
  type UpdateProviderPayload,
} from '@services/providers/dtos/update.dto';
import { providerService } from '@services/providers/service';
import type { GetProviderByIdOutput } from '@services/providers/types/getById.type';
import type { UpdateProviderOutput } from '@services/providers/types/update.type';

function providerQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.providers.detail(id),
    queryFn: () => providerService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute('/_layout/(private)/providers/$id/update')(
  {
    component: ProviderUpdate,
    loader: ({ context, params }) =>
      context.queryClient.ensureQueryData({
        ...providerQueryOptions(params.id),
        revalidateIfStale: true,
      }),
    pendingComponent: () => (
      <div className="flex min-h-dvh w-full items-center justify-center">
        <LoaderCircleIcon className="text-primary animate-spin" size={80} />
      </div>
    ),
    pendingMs: 300,
    pendingMinMs: 600,
  },
);

function ProviderUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();

  const {
    data: { provider },
  } = useSuspenseQuery(providerQueryOptions(id));

  const form = useForm<UpdateProviderDto>({
    resolver: zodResolver(updateProviderSchema),
    mode: 'onBlur',
    defaultValues: {
      capacity: provider.capacity,
      email: provider.email,
      firstName: provider.firstName,
      language: provider.language,
      lastName: provider.lastName,
      phone: provider.phone,
      specialties: provider.specialties ?? [],
    },
  });

  const { isPending, mutate: updateProvider } = useMutation<
    UpdateProviderOutput,
    unknown,
    UpdateProviderPayload
  >({
    mutationFn: providerService.update,
    onError: handleRequestError,
    onSuccess: async ({ provider: updatedProvider }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.providers.detail(id),
      });

      queryClient.setQueryData<GetProviderByIdOutput>(
        queryKeys.providers.detail(id),
        oldData => {
          if (!oldData) {
            return oldData;
          }

          return { provider: updatedProvider };
        },
      );

      queryClient.invalidateQueries({ queryKey: queryKeys.providers.all });

      invalidateSmartEventTimeline({
        id,
        queryClient,
        type: ESmartEventTimelineKeyType.Provider,
      });

      navigate({ to: '..', replace: true });
      toast.success('Success', {
        description: 'Provider updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateProvider({
      id,
      capacity: values.capacity,
      email: values.email,
      firstName: values.firstName,
      language: values.language,
      lastName: values.lastName,
      phone: values.phone,
      specialties: values.specialties,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Update Provider" to=".." />

      <form
        className="mx-auto mt-5 space-y-3 p-2 md:w-4/5 2xl:w-3/5"
        onSubmit={handleSubmit}
      >
        <div className="grid h-fit items-start gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <InputControlled label="First name" name="firstName" />
          <InputControlled label="Last name" name="lastName" />
          <InputControlled label="Email" name="email" type="email" />
          <InputControlled label="Phone" name="phone" variant="phone" />
          <InputControlled
            label="Language"
            name="language"
            options={languageOptions}
            variant="select"
          />
          <InputControlled label="Max capacity" name="capacity" type="number" />
          <InputControlled
            label="Specialties"
            name="specialties"
            options={providerSpecialtyOptions}
            placeholder="Select specialties"
            variant="multiSelect"
          />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            rounded="lg"
            size="lg"
            to=".."
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
